import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditLocationAltOutlinedIcon from '@mui/icons-material/EditLocationAltOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LandscapeOutlinedIcon from '@mui/icons-material/LandscapeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

/**
 * Structure utilisée lorsqu'aucune fiche n'a encore été récupérée.
 */
export const createEmptyStationProfile = () => ({
  /*
   * Informations personnelles propres à l'utilisateur.
   */
  name: '',
  isFavorite: false,
  description: '',
  photoUrl: '',

  /*
   * Informations communes à tous les utilisateurs.
   */
  locationName: '',
  address: '',
  latitude: '',
  longitude: '',
  environmentName: '',
  environmentData: {},

  /*
   * Informations prévues pour les futures fonctionnalités.
   */
  notes: [],
  photos: [],
  alertThresholds: {},
});

/**
 * Garantit que toutes les propriétés attendues existent,
 * même lorsque le backend renvoie une fiche partielle.
 */
const normalizeStationProfile = (stationData) => ({
  ...createEmptyStationProfile(),
  ...(stationData || {}),

  environmentData:
    stationData?.environmentData &&
      typeof stationData.environmentData === 'object' &&
      !Array.isArray(stationData.environmentData)
      ? stationData.environmentData
      : {},

  notes: Array.isArray(stationData?.notes)
    ? stationData.notes
    : [],

  photos: Array.isArray(stationData?.photos)
    ? stationData.photos
    : [],

  alertThresholds:
    stationData?.alertThresholds &&
      typeof stationData.alertThresholds === 'object' &&
      !Array.isArray(stationData.alertThresholds)
      ? stationData.alertThresholds
      : {},
});

/**
 * Bouton utilisé pour les différentes actions de la fiche.
 */
const ActionButton = ({
  icon,
  label,
  onClick,
  disabled = false,
}) => {
  return (
    <Button
      type="button"
      variant="outlined"
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: '100%',
        minWidth: 0,
        minHeight: '78px',
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        borderRadius: '14px',
        borderColor: 'rgba(96, 165, 250, 0.35)',
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
        color: '#dbeafe',
        textTransform: 'none',

        '&:hover': {
          borderColor: '#60a5fa',
          backgroundColor: 'rgba(59, 130, 246, 0.16)',
        },

        '&.Mui-disabled': {
          color: 'rgba(219, 234, 254, 0.4)',
          borderColor: 'rgba(96, 165, 250, 0.12)',
          backgroundColor: 'rgba(59, 130, 246, 0.03)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          color: 'inherit',

          '& svg': {
            fontSize: '26px',
          },
        }}
      >
        {icon}
      </Box>

      <Typography
        component="span"
        sx={{
          fontSize: '11px',
          lineHeight: 1.2,
          fontWeight: 700,
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>
    </Button>
  );
};

/**
 * Bloc d'affichage d'une information de localisation.
 */
const InformationBlock = ({
  icon,
  title,
  value,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '11px',
      }}
    >
      <Box
        sx={{
          width: '42px',
          height: '42px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '12px',
          color: '#60a5fa',
          backgroundColor: 'rgba(59, 130, 246, 0.12)',
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: '#94a3b8',
            fontSize: '12px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            marginTop: '4px',
            color: '#f8fafc',
            fontSize: '15px',
            fontWeight: 700,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const MAX_IMAGE_FILE_SIZE_MB = 8;
const MAX_IMAGE_WIDTH = 1200;
const MAX_IMAGE_HEIGHT = 900;
const IMAGE_COMPRESSION_QUALITY = 0.75;

const resizeImageFileToDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      image.src = reader.result;
    };

    reader.onerror = () => {
      reject(
        new Error('Impossible de lire le fichier image.')
      );
    };

    image.onload = () => {
      let { width, height } = image;

      if (
        width > MAX_IMAGE_WIDTH ||
        height > MAX_IMAGE_HEIGHT
      ) {
        const widthRatio = MAX_IMAGE_WIDTH / width;
        const heightRatio = MAX_IMAGE_HEIGHT / height;
        const ratio = Math.min(widthRatio, heightRatio);

        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');

      if (!context) {
        reject(
          new Error(
            'Impossible de préparer la compression de l’image.'
          )
        );
        return;
      }

      context.drawImage(image, 0, 0, width, height);

      /*
       * Les GIF animés seront convertis en image fixe.
       * Pour garder les GIF animés, il faudra passer par
       * un vrai système d’upload fichier côté backend/S3.
       */
      const compressedDataUrl = canvas.toDataURL(
        'image/jpeg',
        IMAGE_COMPRESSION_QUALITY
      );

      resolve(compressedDataUrl);
    };

    image.onerror = () => {
      reject(
        new Error(
          'Le fichier sélectionné ne peut pas être chargé comme image.'
        )
      );
    };

    reader.readAsDataURL(file);
  });
};

const StationDetailsModal = ({
  stationId = '',
  stationType = '',
  stationData,
  onStationDataChange,
  onOpenAlertSettings,
  isLoading = false,
  loadError = '',
  open,
  onClose,
  hideTriggerButton = false,
  onOpenStationNotes,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlledOpen = typeof open === 'boolean';
  const isOpen = isControlledOpen ? open : internalOpen;

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [actionError, setActionError] = useState('');

  const [draft, setDraft] = useState(
    normalizeStationProfile(stationData)
  );

  const currentProfile = normalizeStationProfile(draft);

  /**
   * Met à jour la fiche lorsqu'une nouvelle station est sélectionnée
   * ou lorsque les données du backend sont chargées.
   */
  useEffect(() => {
    setDraft(normalizeStationProfile(stationData));
    setIsEditing(false);
    setActionError('');
  }, [stationId, stationType, stationData]);

  const handleOpen = () => {
    setDraft(normalizeStationProfile(stationData));
    setIsEditing(false);
    setActionError('');
    if (!isControlledOpen) {
      setInternalOpen(true);
    }
  };

  const handleClose = () => {
    if (isSaving) {
      return;
    }

    setDraft(normalizeStationProfile(stationData));
    setIsEditing(false);
    setActionError('');
    if (isControlledOpen) {
      onClose?.();
    } else {
      setInternalOpen(false);
    }
  };

  const handleOpenAlertSettings = () => {
    if (!stationType || !stationId) {
      setActionError(
        'Impossible d’ouvrir les alertes : aucune station valide n’est sélectionnée.'
      );
      return;
    }

    if (typeof onOpenAlertSettings !== 'function') {
      setActionError(
        'La navigation vers les seuils d’alerte n’est pas disponible.'
      );
      return;
    }

    /*
     * Ferme la modal avant de changer de page.
     */
    if (isControlledOpen) {
      onClose?.();
    } else {
      setInternalOpen(false);
    }
    setIsEditing(false);
    setActionError('');

    /*
     * Envoie à App.js le type et l’identifiant
     * de la station actuellement affichée.
     */
    onOpenAlertSettings({
      stationType: String(stationType).toLowerCase(),
      stationId: String(stationId),
    });
  };

  const handleOpenNotesModal = () => {
  if (!stationType || !stationId) {
    setActionError(
      'Impossible d’ouvrir les notes : aucune station valide n’est sélectionnée.'
    );
    return;
  }

  if (typeof onOpenStationNotes !== 'function') {
    setActionError(
      'La modal de notes n’est pas disponible.'
    );
    return;
  }

  setActionError('');

  /*
   * On ferme la fiche station avant d’ouvrir les notes.
   */
  if (typeof onClose === 'function') {
    onClose();
  }

  onOpenStationNotes({
    table: String(stationType).toLowerCase(),
    id: String(stationId),
  });
};

  const handleStartEditing = () => {
    if (isLoading || isSaving) {
      return;
    }

    setDraft(normalizeStationProfile(stationData));
    setActionError('');
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    if (isSaving) {
      return;
    }

    setDraft(normalizeStationProfile(stationData));
    setActionError('');
    setIsEditing(false);
  };

  const handleFieldChange = (field, value) => {
    setDraft((previousDraft) => ({
      ...previousDraft,
      [field]: value,
    }));
  };

  /**
   * Enregistre la fiche en appelant la fonction async
   * transmise par App.js.
   */
  const handleSave = async () => {
    if (!onStationDataChange || isSaving) {
      return;
    }

    setIsSaving(true);
    setActionError('');

    try {
      const profileToSave = {
        ...draft,

        name: draft.name || '',
        isFavorite: draft.isFavorite === true,
        description: draft.description || '',
        photoUrl: draft.photoUrl || '',

        locationName: draft.locationName || '',
        address: draft.address || '',

        latitude:
          draft.latitude === null ||
            draft.latitude === undefined
            ? ''
            : draft.latitude,

        longitude:
          draft.longitude === null ||
            draft.longitude === undefined
            ? ''
            : draft.longitude,

        environmentName: draft.environmentName || '',

        environmentData:
          draft.environmentData &&
            typeof draft.environmentData === 'object' &&
            !Array.isArray(draft.environmentData)
            ? draft.environmentData
            : {},

        notes: Array.isArray(draft.notes)
          ? draft.notes
          : [],

        photos: Array.isArray(draft.photos)
          ? draft.photos
          : [],

        alertThresholds:
          draft.alertThresholds &&
            typeof draft.alertThresholds === 'object' &&
            !Array.isArray(draft.alertThresholds)
            ? draft.alertThresholds
            : {},
      };

      const savedProfile =
        await onStationDataChange(profileToSave);

      setDraft(
        normalizeStationProfile(
          savedProfile || profileToSave
        )
      );

      setIsEditing(false);
    } catch (error) {
      console.error(
        'Erreur lors de la sauvegarde de la fiche station :',
        error
      );

      setActionError(
        error?.message ||
        'Impossible d’enregistrer la fiche de la station.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditKeyDown = (event) => {
    if (!isEditing || isSaving || isLoading) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    /*
     * Permet de garder Shift + Entrée pour les retours à la ligne.
     */
    if (event.shiftKey) {
      return;
    }

    /*
     * Évite de sauvegarder lorsqu'on valide une composition clavier
     * sur certains claviers ou méthodes de saisie.
     */
    if (event.nativeEvent?.isComposing) {
      return;
    }

    /*
     * Évite de déclencher la sauvegarde si le focus est déjà sur un bouton.
     */
    if (event.target?.closest?.('button')) {
      return;
    }

    event.preventDefault();
    handleSave();
  };

  /**
   * Le favori est enregistré immédiatement dans le backend.
   */
  const handleFavoriteChange = async () => {
    if (
      !onStationDataChange ||
      isSaving ||
      isLoading
    ) {
      return;
    }

    const previousProfile =
      normalizeStationProfile(currentProfile);

    const updatedProfile = {
      ...previousProfile,
      isFavorite: !previousProfile.isFavorite,
    };

    setDraft(updatedProfile);
    setIsSaving(true);
    setActionError('');

    try {
      const savedProfile =
        await onStationDataChange(updatedProfile);

      setDraft(
        normalizeStationProfile(
          savedProfile || updatedProfile
        )
      );
    } catch (error) {
      setDraft(previousProfile);

      console.error(
        'Erreur lors de la modification du favori :',
        error
      );

      setActionError(
        error?.message ||
        'Impossible de modifier le statut favori.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Prévisualisation locale d'une image.
   *
   * Pour la production, il faudra envoyer le fichier vers
   * un stockage puis enregistrer son URL.
   */
  const handlePhotoFileChange = async (event) => {
  const selectedFile = event.target.files?.[0];

  /*
   * Permet de sélectionner deux fois le même fichier
   * si l'utilisateur veut retenter après une erreur.
   */
  event.target.value = '';

  if (!selectedFile) {
    return;
  }

  if (!selectedFile.type.startsWith('image/')) {
    setActionError(
      'Le fichier sélectionné doit être une image.'
    );
    return;
  }

  const fileSizeInMb =
    selectedFile.size / 1024 / 1024;

  if (fileSizeInMb > MAX_IMAGE_FILE_SIZE_MB) {
    setActionError(
      `L’image est trop lourde. Taille maximale autorisée : ${MAX_IMAGE_FILE_SIZE_MB} Mo.`
    );
    return;
  }

  setActionError('');

  try {
    const compressedImage =
      await resizeImageFileToDataUrl(selectedFile);

    handleFieldChange('photoUrl', compressedImage);
  } catch (error) {
    setActionError(
      error.message ||
        'Impossible de préparer l’image sélectionnée.'
    );
  }
};

  const handleRemovePhoto = () => {
  if (isSaving) {
    return;
  }

  setActionError('');
  handleFieldChange('photoUrl', '');
};

  const displayedName =
    String(currentProfile.name || '').trim() ||
    'Nom de la station à définir';

  const displayedLocation =
    String(currentProfile.locationName || '').trim() ||
    'Localisation non définie';

  const displayedAddress =
    String(currentProfile.address || '').trim() ||
    'Adresse non définie';

  const displayedEnvironment =
    String(
      currentProfile.environmentName || ''
    ).trim() ||
    'Aucun environnement défini';

  const displayedDescription =
    String(currentProfile.description || '').trim() ||
    'Aucune description n’a encore été renseignée dans les notes de cette station.';

  const hasCoordinates =
    currentProfile.latitude !== '' &&
    currentProfile.latitude !== null &&
    currentProfile.latitude !== undefined &&
    currentProfile.longitude !== '' &&
    currentProfile.longitude !== null &&
    currentProfile.longitude !== undefined;

  const displayedCoordinates = hasCoordinates
    ? `${currentProfile.latitude}, ${currentProfile.longitude}`
    : 'Coordonnées GPS non définies';

  const displayedError =
    actionError || loadError || '';

  return (
    <>
      {!hideTriggerButton && (
        <Button
          type="button"
          variant="contained"
          startIcon={<SensorsOutlinedIcon />}
          onClick={handleOpen}
          disabled={!stationId}
          sx={{
            minHeight: '44px',
            borderRadius: '12px',
            paddingX: '20px',
            textTransform: 'none',
            fontWeight: 700,
            backgroundColor: '#3b82f6',

            '&:hover': {
              backgroundColor: '#2563eb',
            },
          }}
        >
          Voir la fiche de la station
        </Button>
      )}

      <Dialog
        open={isOpen}
        onClose={handleClose}
        fullScreen={isMobile}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        disableEscapeKeyDown={isSaving}
        PaperProps={{
          sx: {
            maxHeight: isMobile ? '100%' : '94vh',
            borderRadius: isMobile ? 0 : '20px',
            backgroundColor: '#202235',
            backgroundImage: 'none',
            color: '#ffffff',
            border:
              '1px solid rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            position: 'relative',
            padding: {
              xs: '16px 58px 16px 18px',
              md: '22px 70px 22px 28px',
            },
            backgroundColor: '#202235',
            borderBottom:
              '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Typography
            component="h2"
            sx={{
              margin: 0,
              fontSize: {
                xs: '20px',
                md: '24px',
              },
              fontWeight: 800,
            }}
          >
            Fiche de la station
          </Typography>

          <Typography
            component="p"
            sx={{
              marginTop: '3px',
              marginBottom: 0,
              color: '#94a3b8',
              fontSize: '13px',
            }}
          >
            Informations de la station actuellement sélectionnée
          </Typography>

          <IconButton
            type="button"
            onClick={handleClose}
            disabled={isSaving}
            aria-label="Fermer la fiche de la station"
            sx={{
              position: 'absolute',
              top: {
                xs: '12px',
                md: '18px',
              },
              right: {
                xs: '12px',
                md: '20px',
              },
              color: '#cbd5e1',
              backgroundColor:
                'rgba(255, 255, 255, 0.06)',

              '&:hover': {
                color: '#ffffff',
                backgroundColor:
                  'rgba(255, 255, 255, 0.12)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          onKeyDown={handleEditKeyDown}
          sx={{
            padding: {
              xs: '18px',
              md: '28px',
            },
            backgroundColor: '#202235',
          }}
        >
          {displayedError && (
            <Alert
              severity="error"
              sx={{
                marginBottom: '20px',
              }}
            >
              {displayedError}
            </Alert>
          )}

          {isLoading ? (
            <Box
              sx={{
                minHeight: '280px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '14px',
              }}
            >
              <CircularProgress size={34} />

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '14px',
                }}
              >
                Chargement de la fiche de la station...
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'minmax(0, 1fr) 260px',
                  },
                  gap: '24px',
                }}
              >
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '48px',
                        height: '48px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '14px',
                        color: '#60a5fa',
                        backgroundColor:
                          'rgba(59, 130, 246, 0.14)',
                      }}
                    >
                      <SensorsOutlinedIcon
                        sx={{ fontSize: '30px' }}
                      />
                    </Box>

                    <Box
                      sx={{
                        flexGrow: 1,
                        minWidth: 0,
                      }}
                    >
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label="Nom personnel de la station"
                          value={draft.name}
                          disabled={isSaving}
                          onChange={(event) =>
                            handleFieldChange(
                              'name',
                              event.target.value
                            )
                          }
                          placeholder="Exemple : Station du salon"
                          helperText="Ce nom sera propre à l’utilisateur connecté."
                          sx={textFieldStyle}
                        />
                      ) : (
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography
                              component="h3"
                              sx={{
                                margin: 0,
                                fontSize: {
                                  xs: '24px',
                                  md: '31px',
                                },
                                fontWeight: 800,
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {displayedName}
                            </Typography>

                            <IconButton
                              type="button"
                              onClick={handleStartEditing}
                              disabled={isSaving}
                              aria-label="Modifier la fiche de la station"
                              sx={{
                                color: '#94a3b8',

                                '&:hover': {
                                  color: '#ffffff',
                                  backgroundColor:
                                    'rgba(255, 255, 255, 0.08)',
                                },
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          <Typography
                            sx={{
                              marginTop: '5px',
                              color: '#94a3b8',
                              fontSize: '13px',
                            }}
                          >
                            Ce nom est personnel et visible
                            uniquement par l’utilisateur connecté.
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  <Button
                    type="button"
                    variant={
                      currentProfile.isFavorite
                        ? 'contained'
                        : 'outlined'
                    }
                    disabled={isSaving || isLoading}
                    startIcon={
                      isSaving ? (
                        <CircularProgress
                          size={18}
                          color="inherit"
                        />
                      ) : currentProfile.isFavorite ? (
                        <StarRoundedIcon />
                      ) : (
                        <StarBorderRoundedIcon />
                      )
                    }
                    onClick={handleFavoriteChange}
                    sx={{
                      marginTop: '18px',
                      minHeight: '42px',
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 700,

                      ...(currentProfile.isFavorite
                        ? {
                          color: '#111827',
                          backgroundColor: '#facc15',

                          '&:hover': {
                            backgroundColor: '#eab308',
                          },
                        }
                        : {
                          color: '#facc15',
                          borderColor:
                            'rgba(250, 204, 21, 0.55)',

                          '&:hover': {
                            borderColor: '#facc15',
                            backgroundColor:
                              'rgba(250, 204, 21, 0.08)',
                          },
                        }),
                    }}
                  >
                    {currentProfile.isFavorite
                      ? 'Station favorite'
                      : 'Définir comme favorite'}
                  </Button>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      marginBottom: '8px',
                      color: '#94a3b8',
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    Identifiant permanent
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Chip
                      label={`ID : ${stationId || 'Non défini'
                        }`}
                      sx={{
                        maxWidth: '100%',
                        color: '#bfdbfe',
                        backgroundColor:
                          'rgba(59, 130, 246, 0.14)',
                        border:
                          '1px solid rgba(96, 165, 250, 0.3)',
                        fontWeight: 700,
                      }}
                    />

                    {stationType && (
                      <Chip
                        label={stationType}
                        sx={{
                          color: '#c4b5fd',
                          backgroundColor:
                            'rgba(139, 92, 246, 0.12)',
                          border:
                            '1px solid rgba(167, 139, 250, 0.25)',
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      marginTop: '16px',
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(2, minmax(0, 1fr))',
                      gap: '10px',
                    }}
                  >
                    <ActionButton
                      icon={<NotificationsActiveOutlinedIcon />}
                      label="Seuils d’alerte"
                      onClick={handleOpenAlertSettings}
                      disabled={isSaving || isLoading}
                    />

                    <ActionButton
  icon={<NoteAltOutlinedIcon />}
  label="Notes et photos"
  onClick={handleOpenNotesModal}
  disabled={isSaving || isLoading}
/>
                  </Box>
                </Box>
              </Box>

              <Divider
                sx={{
                  marginY: {
                    xs: '22px',
                    md: '28px',
                  },
                  borderColor:
                    'rgba(255, 255, 255, 0.08)',
                }}
              />

              <Box
                sx={{
                  padding: {
                    xs: '16px',
                    md: '20px',
                  },
                  borderRadius: '16px',
                  backgroundColor: '#292b40',
                  border:
                    '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      lg: 'minmax(0, 1fr) 260px',
                    },
                    gap: '22px',
                    alignItems: 'center',
                  }}
                >
                  {isEditing ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(2, minmax(0, 1fr))',
                        },
                        gap: '16px',
                      }}
                    >
                      <TextField
                        fullWidth
                        label="Nom de la localisation"
                        value={draft.locationName}
                        disabled={isSaving}
                        onChange={(event) =>
                          handleFieldChange(
                            'locationName',
                            event.target.value
                          )
                        }
                        placeholder="Exemple : Ma maison > Salon"
                        sx={textFieldStyle}
                      />

                      <TextField
                        fullWidth
                        label="Adresse"
                        value={draft.address}
                        disabled={isSaving}
                        onChange={(event) =>
                          handleFieldChange(
                            'address',
                            event.target.value
                          )
                        }
                        placeholder="Exemple : 10 rue Exemple, Toulouse"
                        sx={textFieldStyle}
                      />

                      <TextField
                        fullWidth
                        label="Latitude"
                        type="number"
                        value={draft.latitude}
                        disabled={isSaving}
                        onChange={(event) =>
                          handleFieldChange(
                            'latitude',
                            event.target.value
                          )
                        }
                        placeholder="43.6047"
                        inputProps={{
                          step: 'any',
                          min: -90,
                          max: 90,
                        }}
                        sx={textFieldStyle}
                      />

                      <TextField
                        fullWidth
                        label="Longitude"
                        type="number"
                        value={draft.longitude}
                        disabled={isSaving}
                        onChange={(event) =>
                          handleFieldChange(
                            'longitude',
                            event.target.value
                          )
                        }
                        placeholder="1.4442"
                        inputProps={{
                          step: 'any',
                          min: -180,
                          max: 180,
                        }}
                        sx={textFieldStyle}
                      />

                      <TextField
                        fullWidth
                        label="Nom de l’environnement"
                        value={draft.environmentName}
                        disabled={isSaving}
                        onChange={(event) =>
                          handleFieldChange(
                            'environmentName',
                            event.target.value
                          )
                        }
                        placeholder="Exemple : Salon principal"
                        sx={textFieldStyle}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(2, minmax(0, 1fr))',
                        },
                        gap: '22px',
                      }}
                    >
                      <InformationBlock
                        icon={<LocationOnOutlinedIcon />}
                        title="Localisation"
                        value={displayedLocation}
                      />

                      <InformationBlock
                        icon={<LocationOnOutlinedIcon />}
                        title="Adresse"
                        value={displayedAddress}
                      />

                      <InformationBlock
                        icon={<MyLocationOutlinedIcon />}
                        title="Coordonnées GPS"
                        value={displayedCoordinates}
                      />

                      <InformationBlock
                        icon={<LandscapeOutlinedIcon />}
                        title="Environnement"
                        value={displayedEnvironment}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(2, minmax(0, 1fr))',
                      gap: '10px',
                    }}
                  >
                    <ActionButton
                      icon={<VisibilityOutlinedIcon />}
                      label="Voir l’environnement"
                      disabled={isSaving}
                    />

                    <ActionButton
                      icon={<EditLocationAltOutlinedIcon />}
                      label="Modifier la position"
                      onClick={handleStartEditing}
                      disabled={isSaving}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ marginTop: '26px' }}>
                <Typography
                  component="h3"
                  sx={{
                    marginBottom: '12px',
                    fontSize: '19px',
                    fontWeight: 800,
                  }}
                >
                  Description de la station
                </Typography>

                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Description"
                    value={draft.description}
                    disabled={isSaving}
                    onChange={(event) =>
                      handleFieldChange(
                        'description',
                        event.target.value
                      )
                    }
                    placeholder="Ajoutez une description de la station..."
                    helperText="Cette description est personnelle à l’utilisateur connecté."
                    sx={textFieldStyle}
                  />
                ) : (
                  <Box
                    sx={{
                      minHeight: '100px',
                      padding: '18px',
                      borderRadius: '15px',
                      backgroundColor: '#292b40',
                      border:
                        '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <Typography
                      sx={{
                        color: currentProfile.description
                          ? '#e2e8f0'
                          : '#94a3b8',
                        lineHeight: 1.7,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {displayedDescription}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ marginTop: '26px' }}>
                <Typography
                  component="h3"
                  sx={{
                    marginBottom: '12px',
                    fontSize: '19px',
                    fontWeight: 800,
                  }}
                >
                  Photo de la station
                </Typography>

                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: {
                      xs: '240px',
                      md: '390px',
                    },
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '18px',
                    backgroundColor: '#181a2a',
                    border: isEditing
                      ? '1px dashed rgba(96, 165, 250, 0.75)'
                      : '1px dashed rgba(148, 163, 184, 0.35)',
                    transition: 'border-color 0.2s ease, background-color 0.2s ease',

                    '&:hover': isEditing
                      ? {
                        backgroundColor: 'rgba(24, 26, 42, 0.92)',
                        borderColor: '#60a5fa',
                      }
                      : {},
                  }}
                >
                  {currentProfile.photoUrl ? (
                    <>
                      <Box
                        component="img"
                        src={currentProfile.photoUrl}
                        alt={`Station ${displayedName}`}
                        sx={{
                          display: 'block',
                          width: '100%',
                          maxHeight: '520px',
                          objectFit: 'contain',
                        }}
                      />

                      {isEditing && (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      flexWrap: 'wrap',
      padding: '16px',
      backgroundColor: 'rgba(15, 23, 42, 0.52)',
      opacity: 0,
      transition: 'opacity 0.2s ease',

      '&:hover': {
        opacity: 1,
      },
    }}
  >
    <Button
      component="label"
      variant="contained"
      disabled={isSaving}
      startIcon={<ImageOutlinedIcon />}
      sx={{
        minHeight: '42px',
        borderRadius: '12px',
        textTransform: 'none',
        fontWeight: 700,
        backgroundColor: '#3b82f6',

        '&:hover': {
          backgroundColor: '#2563eb',
        },
      }}
    >
      Changer la photo

      <input
        hidden
        type="file"
        accept="image/*"
        onChange={handlePhotoFileChange}
      />
    </Button>

    <Button
      type="button"
      variant="outlined"
      disabled={isSaving}
      startIcon={<DeleteOutlineIcon />}
      onClick={handleRemovePhoto}
      sx={{
        minHeight: '42px',
        borderRadius: '12px',
        textTransform: 'none',
        fontWeight: 700,
        color: '#fecaca',
        borderColor: 'rgba(248, 113, 113, 0.7)',
        backgroundColor: 'rgba(127, 29, 29, 0.28)',

        '&:hover': {
          borderColor: '#f87171',
          backgroundColor: 'rgba(127, 29, 29, 0.45)',
        },
      }}
    >
      Supprimer
    </Button>
  </Box>
)}
                    </>
                  ) : (
                    <Box
                      component={isEditing ? 'label' : 'div'}
                      sx={{
                        width: '100%',
                        minHeight: {
                          xs: '240px',
                          md: '390px',
                        },
                        padding: '30px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        color: '#64748b',
                        cursor: isEditing && !isSaving ? 'pointer' : 'default',
                      }}
                    >
                      <ImageOutlinedIcon
                        sx={{
                          fontSize: '70px',
                          color: isEditing ? '#60a5fa' : '#475569',
                        }}
                      />

                      <Typography
                        sx={{
                          marginTop: '10px',
                          color: isEditing ? '#dbeafe' : '#94a3b8',
                          fontWeight: 700,
                        }}
                      >
                        {isEditing
                          ? 'Importer une photo'
                          : 'Aucune photo définie'}
                      </Typography>

                      <Typography
                        sx={{
                          marginTop: '5px',
                          maxWidth: '420px',
                          fontSize: '13px',
                          color: '#64748b',
                        }}
                      >
                        {isEditing
                          ? 'Cliquez dans cette zone pour choisir une image depuis votre ordinateur.'
                          : 'Une photo importée depuis votre ordinateur sera affichée ici.'}
                      </Typography>

                      {isEditing && (
                        <>
                          <Button
                            component="span"
                            variant="outlined"
                            disabled={isSaving}
                            startIcon={<ImageOutlinedIcon />}
                            sx={{
                              marginTop: '18px',
                              minHeight: '42px',
                              borderRadius: '12px',
                              textTransform: 'none',
                              fontWeight: 700,
                              color: '#dbeafe',
                              borderColor: 'rgba(96, 165, 250, 0.55)',

                              '&:hover': {
                                borderColor: '#60a5fa',
                                backgroundColor: 'rgba(59, 130, 246, 0.12)',
                              },
                            }}
                          >
                            Choisir une photo
                          </Button>

                          <input
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoFileChange}
                          />
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: {
              xs: '14px 18px',
              md: '18px 28px',
            },
            gap: '10px',
            backgroundColor: '#202235',
            borderTop:
              '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          {isEditing ? (
            <>
              <Button
                type="button"
                variant="outlined"
                onClick={handleCancelEditing}
                disabled={isSaving}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                }}
              >
                Annuler
              </Button>

              <Button
                type="button"
                variant="contained"
                onClick={handleSave}
                disabled={isSaving || isLoading}
                startIcon={
                  isSaving ? (
                    <CircularProgress
                      size={18}
                      color="inherit"
                    />
                  ) : null
                }
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                {isSaving
                  ? 'Enregistrement...'
                  : 'Enregistrer'}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={handleStartEditing}
                disabled={isSaving || isLoading}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                }}
              >
                Modifier la fiche
              </Button>

              <Button
                type="button"
                variant="contained"
                onClick={handleClose}
                disabled={isSaving}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Fermer
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

const textFieldStyle = {
  '& .MuiInputLabel-root': {
    color: '#94a3b8',
  },

  '& .MuiInputLabel-root.Mui-focused': {
    color: '#60a5fa',
  },

  '& .MuiOutlinedInput-root': {
    color: '#ffffff',
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',

    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.16)',
    },

    '&:hover fieldset': {
      borderColor: 'rgba(96, 165, 250, 0.55)',
    },

    '&.Mui-focused fieldset': {
      borderColor: '#60a5fa',
    },

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.55)',
    },
  },

  '& .MuiFormHelperText-root': {
    color: '#64748b',
  },

  '& input[type="number"]': {
    MozAppearance: 'textfield',
  },

  '& input[type="number"]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },

  '& input[type="number"]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
};

export default StationDetailsModal;